import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, ShieldAlert, AlertTriangle, AlertCircle, 
  Search, Building2, UserCheck, CheckCircle2, XCircle, 
  Download, Copy, Check, ArrowLeft, RefreshCw, Sparkles, 
  Globe, FileText, Scale, Landmark, CreditCard, ChevronRight, X
} from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import TelegramExportButton from './TelegramExportButton';
import { autoDownloadReport } from '../utils/autoDownloader';

interface EuCounterpartyAuditorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
  initialQuery?: string;
}

interface CounterpartyReportData {
  query: string;
  resolvedNip?: string;
  resolvedKrs?: string;
  resolvedCountryCode?: string;
  resolvedVatNumber?: string;
  companyName: string;
  vies?: {
    isValid: boolean;
    countryCode: string;
    vatNumber: string;
    name: string;
    address: string;
    requestDate: string;
    requestIdentifier?: string;
    rawError?: string;
  } | null;
  bialaLista?: {
    name: string;
    nip: string;
    statusVat: string;
    regon?: string;
    krs?: string;
    residenceAddress?: string | null;
    workingAddress?: string | null;
    representatives: Array<{ name: string; pesel?: string | null; nip?: string | null }>;
    authorizedClerks: Array<{ name: string; pesel?: string | null; nip?: string | null }>;
    partners: Array<{ name: string; pesel?: string | null; nip?: string | null }>;
    registrationLegalDate?: string | null;
    accountNumbers: string[];
    hasVirtualAccounts: boolean;
    requestId: string;
    requestDateTime: string;
  } | null;
  krs?: {
    numerKRS: string;
    nazwa: string;
    formaPrawna: string;
    dataRejestracji: string;
    kapitalZakladowy?: string;
    sposobReprezentacji?: string;
    zarzad: Array<{ imiona: string; nazwisko: string; funkcja: string; czyZawieszony?: boolean }>;
    prokurenci: Array<{ imiona: string; nazwisko: string; funkcja: string }>;
    radaNadzorcza: Array<{ imiona: string; nazwisko: string; funkcja: string }>;
    postepowanieUpadlosciowe: boolean;
    likwidacja: boolean;
    zawieszenie: boolean;
    sprawozdaniaFinansowe: Array<{ okres: string; dataZlozenia: string }>;
    ostatniWpisData?: string;
    sadRejestrowy?: string;
  } | null;
  accountCheck?: {
    accountNumber: string;
    isOnWhiteList: boolean;
    statusMessage: string;
  } | null;
  riskScore: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  riskBadges: Array<{ type: 'success' | 'warning' | 'danger' | 'info'; label: string; description: string }>;
  aiSynthesisMarkdown: string;
  contractClauses: {
    uk: string;
    pl: string;
    en: string;
  };
  verifiedAt: string;
}

const DEMO_PRESETS = [
  {
    titleUk: '🇵🇱 ORLEN S.A. (Концерн, Spółka Akcyjna)',
    titlePl: '🇵🇱 ORLEN S.A. (Koncern paliwowy, Spółka Akcyjna)',
    titleEn: '🇵🇱 ORLEN S.A. (Energy Corp, Joint-Stock Company)',
    query: '7740001454',
    account: '40124010661111001156329830',
    descUk: 'NIP: 7740001454, KRS: 0000028860 (VIES + Biała Lista + KRS + 200+ рахунків)',
    descPl: 'NIP: 7740001454, KRS: 0000028860 (VIES + Biała Lista + KRS + 200+ rachunków)',
    descEn: 'NIP: 7740001454, KRS: 0000028860 (VIES + White List + KRS + 200+ accounts)'
  },
  {
    titleUk: '🎮 CD PROJEKT S.A. (IT & GameDev)',
    titlePl: '🎮 CD PROJEKT S.A. (IT & GameDev)',
    titleEn: '🎮 CD PROJEKT S.A. (IT & Game Development)',
    query: '7342867148',
    account: '',
    descUk: 'NIP: 7342867148, KRS: 0000006865 (Spółka Akcyjna, Біржа GPW)',
    descPl: 'NIP: 7342867148, KRS: 0000006865 (Spółka Akcyjna, Giełda GPW)',
    descEn: 'NIP: 7342867148, KRS: 0000006865 (Joint-Stock, Warsaw Stock Exchange GPW)'
  },
  {
    titleUk: '📱 NOKIA OYJ (Фінляндія, EU VAT VIES)',
    titlePl: '📱 NOKIA OYJ (Finlandia, VIES VAT-UE)',
    titleEn: '📱 NOKIA OYJ (Finland, EU VAT VIES)',
    query: 'FI01120389',
    account: '',
    descUk: 'VAT ID: FI01120389 (Транскордонна перевірка ЄС у системі VIES)',
    descPl: 'VAT ID: FI01120389 (Weryfikacja transgraniczna UE w systemie VIES)',
    descEn: 'VAT ID: FI01120389 (EU cross-border validation via VIES)'
  },
  {
    titleUk: '🛍️ ALLEGRO SP. Z O.O. (E-Commerce Лідер)',
    titlePl: '🛍️ ALLEGRO SP. Z O.O. (Lider E-Commerce)',
    titleEn: '🛍️ ALLEGRO SP. Z O.O. (E-Commerce Leader)',
    query: '5252674792',
    account: '',
    descUk: 'NIP: 5252674792, KRS: 0000635005 (Spółka z o.o.)',
    descPl: 'NIP: 5252674792, KRS: 0000635005 (Spółka z o.o.)',
    descEn: 'NIP: 5252674792, KRS: 0000635005 (Polish Limited Liability Co.)'
  }
];

const LOADING_STEPS = {
  uk: [
    "Підключення до офіційного шлюзу реєстрів Європейського Союзу та Польщі...",
    "Запит до бази Єврокомісії VIES (перевірка чинності EU VAT ID для ставки 0% WDT/WNT)...",
    "Запит до Білого Списку платників ПДВ Мінфіну Польщі (Biała Lista Podatników VAT)...",
    "Запит до Судового реєстру Мін'юсту Польщі (KRS: склад Правління, правила підпису)...",
    "Перевірка банківських рахунків для безпечного проведення платежів за процедурою Split Payment...",
    "ШІ-генерація юридичного висновку Due Diligence та захисних застережень до договору..."
  ],
  pl: [
    "Nawiązywanie połączenia z oficjalnymi rejestrami Unii Europejskiej i Polski...",
    "Weryfikacja w systemie VIES Komisji Europejskiej (aktywność VAT-UE do stawki 0% WNT/WDT)...",
    "Sprawdzanie w Wykazie Podatników VAT Ministerstwa Finansów (Biała Lista)...",
    "Pobieranie aktualnego odpisu z Krajowego Rejestru Sądowego (KRS: Zarząd, reprezentacja)...",
    "Weryfikacja rachunku bankowego pod kątem Mechanizmu Podzielonej Płatności (Split Payment)...",
    "Synteza Due Diligence AI i generowanie klauzul zabezpieczających do umowy..."
  ],
  en: [
    "Connecting to official European Union & Polish government registries...",
    "Querying European Commission VIES database (validating EU VAT status for 0% intra-community trade)...",
    "Checking Polish Ministry of Finance White List of VAT Payers (Biała Lista)...",
    "Retrieving active excerpt from Polish Court Register (KRS: Board members, representation rules)...",
    "Validating bank accounts for mandatory Split Payment mechanism compliance...",
    "AI synthesis of comprehensive Due Diligence Dossier and contract safeguard clauses..."
  ]
};

export default function EuCounterpartyAuditor({
  language,
  onBack,
  onGoHome,
  initialQuery = ''
}: EuCounterpartyAuditorProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';

  const [query, setQuery] = useState(initialQuery);
  const [bankAccount, setBankAccount] = useState('');
  const [targetRegister, setTargetRegister] = useState<'ALL' | 'VIES' | 'BIALA_LISTA' | 'KRS' | 'CEIDG'>('ALL');
  const [isAuditing, setIsAuditing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<CounterpartyReportData | null>(null);
  const [activeTab, setActiveTab] = useState<'dossier' | 'krs_board' | 'accounts' | 'clauses'>('dossier');
  const [copied, setCopied] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');

  // Cycle loading steps during verification
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = LOADING_STEPS[language] || LOADING_STEPS.uk;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 3500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, language]);

  const handleRunAudit = async (customQuery?: string, customAccount?: string) => {
    const q = customQuery !== undefined ? customQuery : query;
    const acc = customAccount !== undefined ? customAccount : bankAccount;

    if (!q.trim()) {
      setError(
        isPl 
          ? 'Proszę podać NIP, KRS, VAT-UE lub nazwę podmiotu do weryfikacji.' 
          : (isUk ? 'Будь ласка, введіть NIP, KRS, EU VAT ID або назву контрагента для перевірки.' : 'Please enter NIP, KRS, EU VAT ID, or company name.')
      );
      return;
    }

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const res = await fetch('/api/eu-registry/verify', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rawQuery: q.trim(),
          bankAccount: acc.trim(),
          language
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || (isPl ? 'Błąd podczas weryfikacji w rejestrach.' : (isUk ? 'Помилка під час перевірки в реєстрах.' : 'Verification request failed.')));
      }

      const data = await res.json();
      setReport(data.report);
      setActiveTab('dossier');
    } catch (err: any) {
      console.error('Audit error:', err);
      setError(err.message || (isPl ? 'Nie udało się zweryfikować kontrahenta.' : (isUk ? 'Не вдалося перевірити контрагента.' : 'Failed to verify counterparty.')));
    } finally {
      setIsAuditing(false);
    }
  };

  const handleApplyPreset = (preset: typeof DEMO_PRESETS[0]) => {
    setQuery(preset.query);
    setBankAccount(preset.account);
    handleRunAudit(preset.query, preset.account);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    const docTitle = `Due_Diligence_${report.companyName.replace(/[^A-Za-z0-9А-Яа-яіІїЇєЄ_]/g, '_')}`;
    const fullMarkdown = `
# ОФІЦІЙНИЙ ВИСНОВОК DUE DILIGENCE & ПЕРЕВІРКИ КОНТРАГЕНТА В РЕЄСТРАХ ЄС / ПОЛЬЩІ
**Дата та час верифікації:** ${new Date(report.verifiedAt).toLocaleString('uk-UA')}
**Об'єкт перевірки:** ${report.companyName}
**Ідентифікатори:** NIP: ${report.resolvedNip || '---'}, KRS: ${report.resolvedKrs || '---'}, EU VAT: ${report.resolvedVatNumber || '---'}
**Рівень ризику:** ${report.riskLevel} (${report.riskScore}/100)

---

## 1. СТАТУС В ОФІЦІЙНИХ РЕЄСТРАХ
- **VIES (Європейський Союз):** ${report.vies?.isValid ? 'ДІЙСНИЙ (VALID) — Платник ПДВ у ЄС' : 'НЕАКТИВНИЙ / НЕ ЗНАЙДЕНО'}
- **Biała Lista Podatników VAT (Мінфін Польщі):** ${report.bialaLista?.statusVat || 'Не визначено'} (Дата реєстрації: ${report.bialaLista?.registrationLegalDate || '---'})
- **KRS (Судовий реєстр Польщі):** ${report.krs?.formaPrawna || 'Sp. z o.o. / S.A.'} (Статутний капітал: ${report.krs?.kapitalZakladowy || 'Не вказано'})
- **Правило підпису (Sposób reprezentacji):** ${report.krs?.sposobReprezentacji || 'Згідно з KSH'}
- **Склад Правління (Zarząd):** ${report.krs?.zarzad?.map(z => `${z.imiona} ${z.nazwisko} (${z.funkcja})`).join(', ') || '---'}

---

## 2. ЕКСПЕРТНИЙ ЮРИДИЧНО-ПОДАТКОВИЙ АУДИТ (GEMINI 3.7 FLASH)
${report.aiSynthesisMarkdown}

---

## 3. ЗАХИСНІ КЛАУЗУЛИ ДЛЯ ДОГОВОРУ
### 🇺🇦 Українська редакція:
${report.contractClauses.uk}

### 🇵🇱 Polska redakcja:
${report.contractClauses.pl}

### 🇬🇧 English version:
${report.contractClauses.en}

---
*Звіт сформовано автоматизованим комплексом перевірки реєстрів Deep Research Hub (${window.location.origin}). Дані отримано в режимі реального часу з офіційних шлюзів VIES, MF Gov PL та MS Gov PL.*
`;

    await autoDownloadReport({
      title: docTitle,
      content: fullMarkdown,
      format: 'docx'
    });
  };

  const handleCopy = () => {
    if (!report) return;
    navigator.clipboard.writeText(report.aiSynthesisMarkdown + '\n\n' + report.contractClauses.uk);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredAccounts = (report?.bialaLista?.accountNumbers || []).filter(acc => 
    !accountSearch || acc.includes(accountSearch.replace(/[^0-9]/g, ''))
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Top Navigation Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="px-3.5 py-1.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-medium transition flex items-center gap-1.5 border border-slate-700"
            >
              🏠 {isPl ? 'Strona główna' : (isUk ? 'На головну' : 'Home')}
            </button>
          )}
          <button
            onClick={onBack}
            className="px-3.5 py-1.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-medium transition flex items-center gap-1.5 border border-slate-700"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {isPl ? '← Powrót' : (isUk ? '← Назад' : '← Back')}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <Globe className="w-3.5 h-3.5" />
            VIES • Biała Lista • KRS • CEIDG
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
            <Sparkles className="w-3.5 h-3.5" />
            Gemini 3.7 Flash
          </span>
        </div>
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-indigo-950/60 border border-slate-800 p-6 sm:p-8 shadow-2xl">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4" />
              {isPl ? 'Moduł Weryfikacji Kontrahentów UE & Polski' : (isUk ? 'Модуль Перевірки Контрагентів ЄС & Польщі' : 'EU & Polish Counterparty Verification Module')}
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              {isPl ? 'Weryfikator Kontrahentów UE & Polski (VIES, KRS, Biała Lista VAT)' : (isUk ? 'Аудитор Контрагентів ЄС & Польщі (VIES, KRS, CEIDG & Biała Lista VAT)' : 'EU & Polish Counterparty Compliance Auditor')}
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              {isPl
                ? 'Błyskawiczna weryfikacja podmiotów gospodarczych w bazach VIES (27 państw UE), Białej Liście VAT Ministerstwa Finansów, KRS i CEIDG. Automatyczna analiza uprawnień Zarządu, walidacja rachunków do Split Payment (MPP) i raport Due Diligence.'
                : (isUk 
                  ? 'Миттєва перевірка європейських та польських контрагентів у реєстрах Єврокомісії (VIES), Мінфіну Польщі (Biała Lista Podatników VAT), Мін\'юсту (KRS) та CEIDG. Авто-аудит права підпису Правління, чинності ПДВ, Split Payment та формування повного Досьє Due Diligence.'
                  : 'Instant due diligence verification of EU and Polish companies across VIES (27 EU states), Ministry of Finance White List VAT, Court Register (KRS) and CEIDG. Automated signing authority and split payment bank validation.')}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
            <div className="px-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700 text-center">
              <span className="block text-[11px] text-slate-400 font-medium">
                {isPl ? 'Czas weryfikacji' : (isUk ? 'Швидкість перевірки' : 'Verification speed')}
              </span>
              <span className="text-emerald-400 font-bold text-base">{isPl ? '~2.5 sekundy' : isUk ? '~2.5 секунди' : '~2.5 seconds'}</span>
            </div>
            <div className="px-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700 text-center">
              <span className="block text-[11px] text-slate-400 font-medium">
                {isPl ? 'Format eksportu' : (isUk ? 'Формат звіту' : 'Report format')}
              </span>
              <span className="text-indigo-300 font-bold text-base">Word / PDF / KEP</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Control Panel */}
      <div className="bg-slate-900/90 rounded-2xl border border-slate-800 p-6 space-y-6 shadow-xl">
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-200 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Search className="w-4 h-4 text-emerald-400" />
                {isPl ? 'Wyszukaj Kontrahenta (NIP, KRS, VAT-UE, REGON lub Nazwa):' : (isUk ? 'Пошук Контрагента (NIP, KRS, EU VAT ID, REGON або Назва):' : 'Search Counterparty (NIP, KRS, EU VAT ID, REGON or Name):')}
              </span>
              <span className="text-xs text-slate-400 font-normal">
                {isPl ? 'Obsługuje 27 państw UE' : (isUk ? 'Підтримує 27 країн ЄС' : 'Supports all 27 EU states')}
              </span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRunAudit()}
                placeholder={isPl ? 'np. 7740001454 (NIP), 0000028860 (KRS), PL7740001454 (VAT-UE), FI01120389 lub ORLEN...' : (isUk ? 'напр. 7740001454 (NIP), 0000028860 (KRS), PL7740001454 (VAT-UE), FI01120389 або ORLEN...' : 'e.g. 7740001454 (NIP), 0000028860 (KRS), PL7740001454 (VAT), FI01120389 or ORLEN...')}
                className="w-full pl-11 pr-10 py-3.5 bg-slate-950 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-base font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition shadow-inner"
              />
              <Search className="w-5 h-5 text-slate-400 absolute left-3.5 top-4" />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3.5 top-4 text-slate-500 hover:text-slate-300 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Optional Bank Account Validation Field */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5 text-cyan-400" />
              {isPl ? 'Rachunek bankowy do walidacji na Białej Liście (Opcjonalnie, 26 cyfr / IBAN):' : (isUk ? 'Розрахунковий рахунок для перевірки у Білому Списку (Опціонально, 26 цифр / IBAN):' : 'Bank Account to match against White List (Optional, 26 digits / IBAN):')}
            </label>
            <input
              type="text"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              placeholder={isPl ? 'np. 40124010661111001156329830 (Sprawdzenie czy rachunek uprawnia do Split Payment i zaliczenia w koszty)' : (isUk ? 'напр. 40124010661111001156329830 (Перевірка чи рахунок дає право на витрати та Split Payment)' : 'e.g. 40124010661111001156329830 (Validates split payment and tax deduction safety)')}
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 placeholder-slate-600 text-sm font-mono focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
            />
          </div>
        </div>

        {/* Demo Presets */}
        <div className="space-y-2 pt-2 border-t border-slate-800/80">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              {isPl ? 'Przykłady demonstracyjne (1-klik):' : (isUk ? 'Реальні приклади контрагентів (1-клік):' : 'Real-world demo presets (1-click):')}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {DEMO_PRESETS.map((preset, idx) => (
              <button
                key={idx}
                onClick={() => handleApplyPreset(preset)}
                className="text-left p-3 rounded-xl bg-slate-950/70 hover:bg-slate-800/80 border border-slate-800 hover:border-emerald-500/40 transition group space-y-1"
              >
                <div className="text-xs font-bold text-slate-200 group-hover:text-emerald-400 transition truncate">
                  {isPl ? preset.titlePl : (isUk ? preset.titleUk : preset.titleEn)}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {isPl ? preset.descPl : (isUk ? preset.descUk : preset.descEn)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
          <button
            onClick={() => handleRunAudit()}
            disabled={isAuditing}
            className="w-full sm:flex-1 py-4 px-6 rounded-xl font-bold text-slate-950 bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-500 hover:from-emerald-300 hover:to-emerald-400 shadow-lg shadow-emerald-500/20 active:scale-[0.99] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 text-base tracking-wide"
          >
            {isAuditing ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>{isPl ? 'Trwa weryfikacja w rejestrach...' : (isUk ? 'Триває перевірка в реєстрах...' : 'Querying official registries...')}</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5" />
                <span>{isPl ? '🚀 Zweryfikuj Kontrahenta w Rejestrach UE / Polski' : (isUk ? '🚀 Запустити Перевірку в Реєстрах ЄС & Польщі' : '🚀 Verify Counterparty across EU & Polish Registries')}</span>
              </>
            )}
          </button>

          <button
            onClick={() => handleApplyPreset(DEMO_PRESETS[0])}
            className="w-full sm:w-auto px-5 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold border border-slate-700 transition flex items-center justify-center gap-2 shrink-0"
          >
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span>{isPl ? '⚡ Przykład Orlen S.A.' : (isUk ? '⚡ Демо Orlen S.A.' : '⚡ Demo Orlen S.A.')}</span>
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 rounded-xl bg-red-950/50 border border-red-800/80 text-red-200 text-sm flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold">{isPl ? 'Wystąpił błąd weryfikacji' : (isUk ? 'Помилка перевірки' : 'Verification error')}</span>
              <p className="text-red-300 text-xs">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Loading Progress State */}
      {isAuditing && (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 space-y-6 text-center animate-pulse shadow-2xl">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400">
            <RefreshCw className="w-8 h-8 animate-spin" />
          </div>
          <div className="space-y-2 max-w-xl mx-auto">
            <h3 className="text-lg font-bold text-white">
              {isPl ? 'Trwa weryfikacja podmiotu w bazach rządowych...' : (isUk ? 'Здійснюється перевірка в офіційних державних реєстрах...' : 'Querying government registries in real-time...')}
            </h3>
            <p className="text-sm text-emerald-400 font-medium">
              {(LOADING_STEPS[language] || LOADING_STEPS.uk)[loadingStepIndex]}
            </p>
          </div>
          <div className="w-full max-w-md mx-auto bg-slate-800 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-emerald-500 to-cyan-400 h-full transition-all duration-500"
              style={{ width: `${((loadingStepIndex + 1) / (LOADING_STEPS[language] || LOADING_STEPS.uk).length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Report Dashboard */}
      {report && !isAuditing && (
        <div className="space-y-6">
          {/* Executive Company Summary Card */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-2xl space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-800">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 rounded-md bg-slate-800 text-slate-200 text-xs font-bold font-mono">
                    NIP: {report.resolvedNip || '---'}
                  </span>
                  {report.resolvedKrs && (
                    <span className="px-3 py-1 rounded-md bg-slate-800 text-slate-200 text-xs font-bold font-mono">
                      KRS: {report.resolvedKrs}
                    </span>
                  )}
                  {report.resolvedVatNumber && (
                    <span className="px-3 py-1 rounded-md bg-indigo-950 text-indigo-300 border border-indigo-800/60 text-xs font-bold font-mono">
                      EU VAT: {report.resolvedCountryCode}{report.resolvedVatNumber}
                    </span>
                  )}
                  <span className="px-2.5 py-0.5 rounded text-[11px] font-semibold bg-slate-800 text-slate-300">
                    {report.krs?.formaPrawna || 'Podmiot gospodarczy'}
                  </span>
                </div>

                <h2 className="text-2xl font-black text-white tracking-tight">
                  {report.companyName}
                </h2>

                <p className="text-xs text-slate-400 flex items-center gap-2">
                  <span>📍 {report.bialaLista?.workingAddress || report.bialaLista?.residenceAddress || report.vies?.address || 'Adres rejestrowy'}</span>
                  <span>•</span>
                  <span>🕒 {new Date(report.verifiedAt).toLocaleString(isPl ? 'pl-PL' : (isUk ? 'uk-UA' : 'en-US'))}</span>
                </p>
              </div>

              {/* Risk Gauge Box */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-950 border border-slate-800 shrink-0">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center font-black text-2xl border ${
                  report.riskLevel === 'LOW' 
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : (report.riskLevel === 'MEDIUM' 
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                      : 'bg-red-500/10 border-red-500/30 text-red-400')
                }`}>
                  {report.riskScore}%
                </div>
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                    {isPl ? 'Ocena Ryzyka (Due Diligence)' : (isUk ? 'Оцінка Благонадійності' : 'Compliance Risk Score')}
                  </span>
                  <span className={`text-base font-extrabold ${
                    report.riskLevel === 'LOW' ? 'text-emerald-400' : (report.riskLevel === 'MEDIUM' ? 'text-amber-400' : 'text-red-400')
                  }`}>
                    {report.riskLevel === 'LOW' 
                      ? (isPl ? '🟢 NISKIE RYZYKO' : (isUk ? '🟢 НИЗЬКИЙ РИЗИК' : '🟢 LOW RISK')) 
                      : (report.riskLevel === 'MEDIUM' 
                        ? (isPl ? '🟡 ŚREDNIE RYZYKO' : (isUk ? '🟡 СЕРЕДНІЙ РИЗИК' : '🟡 MEDIUM RISK')) 
                        : (isPl ? '🔴 WYSOKIE RYZYKO' : (isUk ? '🔴 ВИСОКИЙ РИЗИК' : '🔴 HIGH RISK')))}
                  </span>
                  <span className="text-[11px] text-slate-400 block">
                    {report.riskBadges.length} {isPl ? 'zweryfikowanych parametrów' : (isUk ? 'перевірених параметрів' : 'verified indicators')}
                  </span>
                </div>
              </div>
            </div>

            {/* Badges Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {report.riskBadges.map((badge, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-xl border flex items-start gap-2.5 ${
                    badge.type === 'success' 
                      ? 'bg-emerald-950/30 border-emerald-800/40 text-emerald-300'
                      : (badge.type === 'danger'
                        ? 'bg-red-950/40 border-red-800/50 text-red-300'
                        : (badge.type === 'warning'
                          ? 'bg-amber-950/30 border-amber-800/40 text-amber-300'
                          : 'bg-slate-800/40 border-slate-700 text-slate-300'))
                  }`}
                >
                  {badge.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : (
                    badge.type === 'danger' ? <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" /> : (
                      badge.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> : <Landmark className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    )
                  )}
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold">{badge.label}</div>
                    <div className="text-[11px] text-slate-400 leading-snug">{badge.description}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Bar (Export & Tools) */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-800">
              {/* Tabs */}
              <div className="flex items-center gap-1.5 p-1 bg-slate-950 rounded-xl border border-slate-800">
                <button
                  onClick={() => setActiveTab('dossier')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                    activeTab === 'dossier' 
                      ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>{isPl ? 'Raport Due Diligence' : (isUk ? 'Досьє Due Diligence' : 'Due Diligence Dossier')}</span>
                </button>

                <button
                  onClick={() => setActiveTab('krs_board')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                    activeTab === 'krs_board' 
                      ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <UserCheck className="w-3.5 h-3.5" />
                  <span>{isPl ? 'Zarząd & Reprezentacja' : (isUk ? 'Правління & Право підпису' : 'Board & Signatories')}</span>
                  {report.krs?.zarzad?.length ? (
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-800 text-white font-mono">
                      {report.krs.zarzad.length}
                    </span>
                  ) : null}
                </button>

                <button
                  onClick={() => setActiveTab('accounts')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                    activeTab === 'accounts' 
                      ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>{isPl ? 'Rachunki Białej Listy' : (isUk ? 'Рахунки Білого Списку' : 'White List Accounts')}</span>
                  {report.bialaLista?.accountNumbers?.length ? (
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-800 text-white font-mono">
                      {report.bialaLista.accountNumbers.length}
                    </span>
                  ) : null}
                </button>

                <button
                  onClick={() => setActiveTab('clauses')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                    activeTab === 'clauses' 
                      ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Scale className="w-3.5 h-3.5" />
                  <span>{isPl ? 'Klauzule Umowne' : (isUk ? 'Клаузули до Договору' : 'Contract Clauses')}</span>
                </button>
              </div>

              {/* Export Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadDocx}
                  className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition flex items-center gap-1.5 border border-slate-700 shadow"
                >
                  <Download className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Word (.docx {isPl ? 'Standard UE' : (isUk ? 'ДСТУ' : 'EU Standard')})</span>
                </button>

                <button
                  onClick={handleCopy}
                  className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition flex items-center gap-1.5 border border-slate-700 shadow"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                  <span>{copied ? (isPl ? 'Skopiowano' : (isUk ? 'Скопійовано' : 'Copied')) : (isPl ? 'Kopiuj' : (isUk ? 'Копіювати' : 'Copy'))}</span>
                </button>

                <TelegramExportButton
                  reportText={report.aiSynthesisMarkdown}
                  agentTitle={`Due Diligence: ${report.companyName}`}
                  language={language}
                />
              </div>
            </div>
          </div>

          {/* Tab 1: AI Due Diligence Dossier */}
          {activeTab === 'dossier' && (
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-xl space-y-6">
              {/* Account Match Alert if checked */}
              {report.accountCheck && (
                <div className={`p-4 rounded-xl border flex items-start gap-3 ${
                  report.accountCheck.isOnWhiteList 
                    ? 'bg-emerald-950/40 border-emerald-800 text-emerald-200' 
                    : 'bg-red-950/40 border-red-800 text-red-200'
                }`}>
                  {report.accountCheck.isOnWhiteList ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  )}
                  <div className="space-y-1">
                    <span className="font-bold text-sm">
                      {isPl ? 'Wynik weryfikacji rachunku bankowego:' : (isUk ? 'Результат перевірки рахунку в Білому списку:' : 'Bank Account White List Result:')} {report.accountCheck.accountNumber}
                    </span>
                    <p className="text-xs">{report.accountCheck.statusMessage}</p>
                  </div>
                </div>
              )}

              {/* Markdown Content */}
              <div className="prose prose-invert prose-emerald max-w-none text-slate-300 text-sm leading-relaxed whitespace-pre-line">
                {report.aiSynthesisMarkdown}
              </div>
            </div>
          )}

          {/* Tab 2: Board & Representation (KRS) */}
          {activeTab === 'krs_board' && (
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-xl space-y-6">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-400" />
                  {isPl ? 'Sposób Reprezentacji i Skład Zarządu (KRS)' : (isUk ? 'Правило Підпису та Склад Органів Управління (KRS)' : 'Signing Rules & Management Board (KRS)')}
                </h3>
                <p className="text-xs text-slate-400">
                  {isPl ? 'Zasady zaciągania zobowiązań i podpisywania umów w imieniu spółki:' : (isUk ? 'Офіційні правила взяття зобов\'язань та підписання договорів від імені компанії:' : 'Official rules of binding obligations and signing contracts:')}
                </p>
              </div>

              {/* Representation Rule Banner */}
              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 text-xs font-mono whitespace-pre-wrap leading-relaxed">
                <span className="text-emerald-400 font-bold block mb-1">
                  ⚖️ {isPl ? 'SPOSÓB REPREZENTACJI (Z KRS):' : (isUk ? 'ПРАВИЛО ПРЕДСТАВНИЦТВА (З РЕЄСТРУ KRS):' : 'MANNER OF REPRESENTATION (KRS):')}
                </span>
                {report.krs?.sposobReprezentacji || (isPl ? 'Brak szczegółowego zapisu (stosuje się przepisy ogólne KSH).' : 'Згідно з установчими документами та загальними нормами Кодексу комерційних товариств (KSH).')}
              </div>

              {/* Zarząd Members */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {isPl ? 'Członkowie Zarządu:' : (isUk ? 'Члени Правління (Zarząd):' : 'Board Members:')}
                </h4>
                {report.krs?.zarzad && report.krs.zarzad.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {report.krs.zarzad.map((member, idx) => (
                      <div key={idx} className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
                        <div className="space-y-0.5">
                          <span className="font-bold text-white text-sm">
                            {member.imiona} {member.nazwisko}
                          </span>
                          <span className="block text-xs text-emerald-400 font-medium">
                            {member.funkcja}
                          </span>
                        </div>
                        {member.czyZawieszony && (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-red-950 text-red-300 border border-red-800 font-semibold">
                            ZAWIESZONY
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-slate-950/50 border border-slate-800 text-slate-500 text-xs">
                    {isPl ? 'Brak danych o Zarządzie w rejestrze KRS (np. jednoosobowa działalność CEIDG).' : 'Дані про колегіальне правління відсутні (наприклад, для ФОП CEIDG).'}
                  </div>
                )}
              </div>

              {/* Rada Nadzorcza & Prokurenci */}
              {report.krs?.prokurenci && report.krs.prokurenci.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-slate-800">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    {isPl ? 'Prokurenci (Upełnomocnieni do podpisu):' : (isUk ? 'Прокуренти (Уповноважені на підпис):' : 'Procurators / Authorized Proxies:')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {report.krs.prokurenci.map((member, idx) => (
                      <div key={idx} className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                        <span className="font-bold text-white text-xs block">
                          {member.imiona} {member.nazwisko}
                        </span>
                        <span className="text-[11px] text-cyan-400 font-medium">
                          {member.funkcja}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: White List Accounts */}
          {activeTab === 'accounts' && (
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-xl space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-cyan-400" />
                    {isPl ? 'Zarejestrowane Rachunki Bankowe (Biała Lista MF)' : (isUk ? 'Зареєстровані Рахунки у Білому Списку Мінфіну Польщі' : 'White List Bank Accounts (Split Payment Verified)')}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {isPl ? 'Tylko płatności na te rachunki chronią przed solidarną odpowiedzialnością za VAT (art. 117ba Ordynacja podatkowa).' : (isUk ? 'Оплата на ці рахунки гарантує включення до витрат та захист від солідарної відповідальності за ПДВ.' : 'Payments to these accounts safeguard tax deductibility and avoid joint VAT liability.')}
                  </p>
                </div>

                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    placeholder={isPl ? 'Filtruj cyfry rachunku...' : (isUk ? 'Швидкий пошук номера...' : 'Filter account numbers...')}
                    className="w-full pl-8 pr-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 font-mono"
                  />
                  <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2.5" />
                </div>
              </div>

              {filteredAccounts.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {filteredAccounts.map((acc, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-3 hover:border-cyan-500/40 transition"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-6 h-6 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center text-[10px] font-mono shrink-0">
                          {idx + 1}
                        </span>
                        <span className="font-mono text-xs text-slate-200 font-bold tracking-wider">
                          {acc.replace(/(.{2})(.{4})(.{4})(.{4})(.{4})(.{4})(.{4})/, '$1 $2 $3 $4 $5 $6 $7')}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(acc);
                          alert(isPl ? `Skopiowano: ${acc}` : (isUk ? `Скопійовано номер: ${acc}` : `Copied: ${acc}`));
                        }}
                        className="px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-medium transition flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        <span>{isPl ? 'Kopiuj' : (isUk ? 'Копіювати' : 'Copy')}</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 rounded-xl bg-slate-950 border border-slate-800 text-center text-slate-500 text-xs">
                  {isPl ? 'Brak zarejestrowanych rachunków lub nie znaleziono dopasowań.' : (isUk ? 'Зареєстровані банківські рахунки не знайдені в Білому списку або не відповідають фільтру.' : 'No registered accounts found or no matches.')}
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Contract Clauses */}
          {activeTab === 'clauses' && (
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-xl space-y-6">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Scale className="w-5 h-5 text-emerald-400" />
                  {isPl ? 'Gotowe Klauzule Zabezpieczające do Umowy' : (isUk ? 'Готові Захисні Клаузули для Включення до Договору' : 'Standardized Protective Contract Clauses')}
                </h3>
                <p className="text-xs text-slate-400">
                  {isPl ? 'Trójjęzyczne klauzule o Białej Liście, Split Payment i prawie reprezentacji do wklejenia do kontraktu:' : (isUk ? 'Тримовні формулювання про Білий список, Split Payment та право підпису для вставки в договір:' : 'Trilingual clauses regarding White List, Split Payment and representation authority to insert into contracts:')}
                </p>
              </div>

              {/* Ukrainian Clause */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                    🇺🇦 {isPl ? 'Wersja ukraińska:' : (isUk ? 'Редакція українською мовою:' : 'Ukrainian version:')}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(report.contractClauses.uk);
                      alert(isPl ? 'Skopiowano treść po ukraińsku' : (isUk ? 'Скопійовано українську редакцію' : 'Copied Ukrainian version'));
                    }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-medium"
                  >
                    <Copy className="w-3 h-3" />
                    <span>{isPl ? 'Kopiuj' : (isUk ? 'Скопіювати' : 'Copy')}</span>
                  </button>
                </div>
                <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-xs leading-relaxed whitespace-pre-wrap font-serif">
                  {report.contractClauses.uk}
                </div>
              </div>

              {/* Polish Clause */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                    🇵🇱 {isPl ? 'Wersja polska:' : (isUk ? 'Редакція польською мовою:' : 'Polish version:')}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(report.contractClauses.pl);
                      alert(isPl ? 'Skopiowano treść po polsku' : (isUk ? 'Скопійовано польську редакцію' : 'Copied Polish version'));
                    }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-medium"
                  >
                    <Copy className="w-3 h-3" />
                    <span>{isPl ? 'Kopiuj' : (isUk ? 'Скопіювати' : 'Copy')}</span>
                  </button>
                </div>
                <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-xs leading-relaxed whitespace-pre-wrap font-serif">
                  {report.contractClauses.pl}
                </div>
              </div>

              {/* English Clause */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                    🇬🇧 {isPl ? 'Wersja angielska:' : (isUk ? 'Редакція англійською мовою:' : 'English version:')}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(report.contractClauses.en);
                      alert(isPl ? 'Skopiowano treść po angielsku' : (isUk ? 'Скопійовано англійську редакцію' : 'Copied English version'));
                    }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-medium"
                  >
                    <Copy className="w-3 h-3" />
                    <span>{isPl ? 'Kopiuj' : (isUk ? 'Скопіювати' : 'Copy')}</span>
                  </button>
                </div>
                <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-xs leading-relaxed whitespace-pre-wrap font-serif">
                  {report.contractClauses.en}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
